import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  addFaultRule,
  getCurrentFault,
  getCurrentFaultRule,
  getFaultCodesList,
  getFaultRulesList,
  getFaultsList,
  updateFaultRule,
} from "./reducer";
import { IFault, IFaultsCode, IFaultsRule } from "Shared/Types/faults";

interface bodyTypeState {
  /** Params of the last table request, reused to reload after edits. */
  lastListParams: Parameters<typeof getFaultsList>[0];
  error?: string | null;
  loading: boolean;
  isFunLoading: boolean;
  success?: boolean | null;
  faultsList: IFault[];
  currentFault: IFault | null;
  faultsCodesList: IFaultsCode[];
  faultsRulesList: IFaultsRule[];
  currentFaultsRule: IFaultsRule | null;
  rulesCount: number;
  /** The rules table's own request state; other fault requests don't touch it. */
  rulesLoading: boolean;
  rulesError: string | null;
  /** Params of the last rules request, reused to reload after edits. */
  lastRulesParams: Parameters<typeof getFaultRulesList>[0];
  faultsNumPage: number;
}

const initialState: bodyTypeState = {
  lastListParams: null,
  loading: false,
  isFunLoading: false,
  error: null,
  faultsList: [],
  currentFault: null,
  faultsCodesList: [],
  faultsRulesList: [],
  currentFaultsRule: null,
  rulesCount: 0,
  rulesLoading: false,
  rulesError: null,
  lastRulesParams: null,
  faultsNumPage: 0,
};

const FaultsSlice = createSlice({
  name: "faults",
  initialState,
  reducers: {
    clear(state) {
      state.error = null;
      state.loading = false;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFaultsList.pending, (state, action) => {
      if (action.meta.arg) state.lastListParams = action.meta.arg;
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(getFaultsList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.faultsList = action.payload.results;
      state.faultsNumPage = action.payload.count;
    });
    builder.addCase(getFaultsList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(getCurrentFault.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(getCurrentFault.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.currentFault = action.payload;
    });
    builder.addCase(getCurrentFault.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(getFaultCodesList.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(getFaultCodesList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.faultsCodesList = action.payload.results;
    });
    builder.addCase(getFaultCodesList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    builder.addCase(getFaultRulesList.pending, (state, action) => {
      state.loading = true;
      state.error = null;
      state.success = false;
      state.rulesLoading = true;
      state.rulesError = null;
      state.lastRulesParams = action.meta.arg;
    });
    builder.addCase(getFaultRulesList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.rulesLoading = false;
      state.faultsRulesList = action.payload.results;
      state.rulesCount = action.payload.count;
    });
    builder.addCase(getFaultRulesList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.rulesLoading = false;
      state.rulesError = action.error.message ?? "Request failed";
    });

    builder.addCase(getCurrentFaultRule.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(getCurrentFaultRule.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.currentFaultsRule = action.payload.results;
    });
    builder.addCase(getCurrentFaultRule.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(addFaultRule.pending, (state) => {
      state.isFunLoading = true;
    });
    builder.addCase(addFaultRule.fulfilled, (state) => {
      state.isFunLoading = false;
    });
    builder.addCase(addFaultRule.rejected, (state, action) => {
      state.loading = false;
      state.isFunLoading = false;

      state.error = action.error.message;
    });
    builder.addCase(updateFaultRule.pending, (state) => {
      state.isFunLoading = true;
    });
    builder.addCase(updateFaultRule.fulfilled, (state) => {
      state.isFunLoading = false;
    });
    builder.addCase(updateFaultRule.rejected, (state, action) => {
      state.loading = false;
      state.isFunLoading = false;
      state.error = action.error.message;
    });
  },
});

export const { clear } = FaultsSlice.actions;

export const selectFaultsList = (state: RootState) => state.faults.faultsList;
export const selectFaultsListLoading = (state: RootState) =>
  state.faults.loading;
export const selectFaultsListIsFuncLoading = (state: RootState) =>
  state.faults.isFunLoading;
export const selectFaultsNumPage = (state: RootState) =>
  state.faults.faultsNumPage;
export const selectCurrentFault = (state: RootState) =>
  state.faults.currentFault;
export const selectFaultsRulesList = (state: RootState) =>
  state.faults.faultsRulesList;
export const selectRulesCount = (state: RootState) => state.faults.rulesCount;
export const selectRulesLoading = (state: RootState) =>
  state.faults.rulesLoading;
export const selectRulesError = (state: RootState) => state.faults.rulesError;
export const selectCurrentFaultsRule = (state: RootState) =>
  state.faults.currentFaultsRule;
export const selectFaultsCodesList = (state: RootState) =>
  state.faults.faultsCodesList;
export const selectFaultsAuthLoading = (state: RootState) =>
  state.faults.loading;
export const selectFaultsAuthError = (state: RootState) => state.faults.error;
export const selectFaultsAuthSuccess = (state: RootState) =>
  state.faults.success;

export const selectFaultsListLastParams = (state: RootState) =>
  state.faults.lastListParams;

export default FaultsSlice;
